import { defineStore } from 'pinia';
import ConfigurationAPI from '@/store/api/configuration';
import {
  CategoricalEmail,
  DryMatterConfiguration,
  FreeConsultRequest,
  HarvestLoadConfiguration,
  LatestSeasonDate,
  PaginatedSeason,
  Season,
  SeasonCalendar,
  SendUserEmail,
  SMTPSetting
} from '@/store/models/configuration';
import { EmailReset } from '@/store/models/jwt';
import { buildQuery } from '@/utils/query';
import { QTablePagination } from '@/store/models/meta';
import { emptyPage } from '@/store/models/page';
import { useCommonStore } from '@/store/modules/common';

export const useConfigurationStore = defineStore('configuration', {
  state: () => ({
    HarvestLoadConfiguration: {} as HarvestLoadConfiguration,
    DryMatterConfiguration: {} as DryMatterConfiguration,
    SMTPSettings: {} as SMTPSetting,
    OldSMTPSettings: {} as SMTPSetting,
    PaginatedSeasonCalendar: emptyPage<PaginatedSeason>(),
    Seasons: [] as Season[],
    AddEditSeasonCalendar: {} as SeasonCalendar,
    LatestSeasonDate: {} as LatestSeasonDate,
    SeasonTablePagination: {
      sortBy: 'id',
      descending: false,
      page: 1,
      rowsPerPage: 10,
      rowsNumber: 1
    } as QTablePagination
  }),
  actions: {
    setAddEditSeasonCalendar(seasonCalendar: SeasonCalendar) {
      this.AddEditSeasonCalendar = seasonCalendar;
    },
    setSeasonTablePagination(data: QTablePagination) {
      this.SeasonTablePagination = data;
    },
    async getHarvestLoadConfigurationByFarmId(id: number) {
      this.HarvestLoadConfiguration = await ConfigurationAPI.getHarvestLoadConfiguration(
        id
      );
    },
    async updateHarvestLoadConfiguration(
      harvestLoadConfiguration: HarvestLoadConfiguration
    ) {
      this.HarvestLoadConfiguration = await ConfigurationAPI.updateHarvestLoadConfiguration(
        harvestLoadConfiguration
      );
    },
    setSMTPSettings(smtpSettings: SMTPSetting) {
      this.SMTPSettings = smtpSettings;
    },
    async getSMTPSettings() {
      const smtpSettings = await ConfigurationAPI.getSMTPSetting();
      this.SMTPSettings = smtpSettings;
      this.OldSMTPSettings = JSON.parse(JSON.stringify(smtpSettings));
    },
    async updateSMTPSettings(settings: SMTPSetting) {
      this.SMTPSettings = await ConfigurationAPI.updateSMTPSetting(settings);
    },
    async testSMTPSetting(email: EmailReset) {
      return await ConfigurationAPI.testSMTPSetting(email);
    },
    async getSeasonCalendars(params: { farm: number }) {
      const query = buildQuery({
        farm: params?.farm
      });
      this.PaginatedSeasonCalendar = await ConfigurationAPI.getSeasonCalendars(
        query
      );
    },
    async getSeasonList(params: { farm: number; cropVariety?: number | null }) {
      // `page_size: '0'` (string) is intentional — bypasses buildQuery's
      // truthy-skip filter so the no-pagination flag is sent verbatim.
      const query = buildQuery({
        page_size: '0',
        season_calendar__farm: params?.farm,
        season_calendar__crop_variety: params?.cropVariety
      });
      this.Seasons = await ConfigurationAPI.getSeasons(query);
    },
    async getSeasonCalendarById(id: number) {
      this.AddEditSeasonCalendar = await ConfigurationAPI.getSeasonCalendarById(
        id
      );
    },
    async getLatestSeasonDateByFarm(id: number) {
      const latestSeasonDate = await ConfigurationAPI.getLatestSeasonDateByFarm(
        id
      );
      const commonStore = useCommonStore();
      // Seeds the window on a first visit, and follows the farm when one is
      // picked on the map. It must not fire for a farm whose window is
      // already in place, or every navigation would wipe a hand-picked
      // From/To - the toolbar asks for this again each time it mounts.
      if (commonStore.FilterFarmId !== id) {
        commonStore.setStartDate(latestSeasonDate.startDate);
        commonStore.setEndDate(latestSeasonDate.endDate);
      }
      this.LatestSeasonDate = latestSeasonDate;
    },
    async createSeasonCalendar(data: SeasonCalendar) {
      this.AddEditSeasonCalendar = await ConfigurationAPI.createSeasonCalendar(
        data
      );
    },
    async updateSeasonCalendar(data: SeasonCalendar) {
      this.AddEditSeasonCalendar = await ConfigurationAPI.updateSeasonCalendar(
        data,
        data.id
      );
    },
    async deleteSeasonCalendar(id: number) {
      await ConfigurationAPI.deleteSeasonCalendar(id);
      if (this.PaginatedSeasonCalendar.results) {
        const idx = this.PaginatedSeasonCalendar.results.findIndex(
          x => x.id == id
        );
        if (idx !== -1) {
          this.PaginatedSeasonCalendar.results.splice(idx, 1);
        }
      }
      this.AddEditSeasonCalendar = {} as SeasonCalendar;
    },
    async sendCategoricalEmail(categoricalEmail: CategoricalEmail) {
      return await ConfigurationAPI.sendCategoricalEmail(categoricalEmail);
    },
    async sendUserEmail(email: SendUserEmail) {
      return await ConfigurationAPI.sendUserEmail(email);
    },
    async sendFreeConsultRequest(freeConsultRequest: FreeConsultRequest) {
      return await ConfigurationAPI.sendFreeConsultRequest(freeConsultRequest);
    },
    async getDryMatterConfigurationByFarmId(id: number) {
      this.DryMatterConfiguration = await ConfigurationAPI.getDryMatterConfiguration(
        id
      );
    },
    async updateDryMatterConfiguration(
      DryMatterConfiguration: DryMatterConfiguration
    ) {
      this.DryMatterConfiguration = await ConfigurationAPI.updateDryMatterConfiguration(
        DryMatterConfiguration
      );
    }
  }
});

